import { HubSpotIcon } from '@/icons/hubspot-icon';
import { HubSpotLogo } from '@/icons/hubspot-logo';
import { NetSuiteIcon } from '@/icons/netsuite-icon';
import { NetSuiteLogo } from '@/icons/netsuite-logo';
import { QuickBooksOnlineIcon } from '@/icons/quickbooks-online-icon';
import { QuickBooksOnlineLogo } from '@/icons/quickbooks-online-logo';
import { SalesforceIcon } from '@/icons/salesforce-icon';
import { SalesforceLogo } from '@/icons/salesforce-logo';
import { SvgIconProps } from '@mui/material';
import { ComponentType } from 'react';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Schemas & types
// ---------------------------------------------------------------------------

/** Schema defining integration system identifiers */
const integrationIdSchema = z.enum([
  'HUBSPOT',
  'SFDC',
  'NETSUITE',
  'QBO',
  'NONE',
]);

export type IntegrationId = z.infer<typeof integrationIdSchema>;

/** Schema defining integration categories */
const integrationCategorySchema = z.enum(['CRM', 'ERP']);

type IntegrationCategory = z.infer<typeof integrationCategorySchema>;

/** Schema for an integration object representing a link to an external integration record */
export const integrationSchema = z.object({
  id: integrationIdSchema,
  type: integrationCategorySchema,
  label: z.string(),
  link: z.string().nullable(),
  isSyncing: z.boolean(),
  needsReview: z.boolean(),
  inError: z.boolean(),
  errorCode: z.number().int().nullable(),
});

export type Integration = z.infer<typeof integrationSchema>;

/** Schema for an array of integrations that gracefully handles null → [] */
export const integrationListSchema = z
  .array(integrationSchema)
  .nullable()
  .catch([])
  .transform((v) => v ?? []);

// ---------------------------------------------------------------------------
// Display metadata
// ---------------------------------------------------------------------------

interface IntegrationMeta {
  label: string;
  category: IntegrationCategory;
  Icon: ComponentType<SvgIconProps>;
  Logo: ComponentType<SvgIconProps>;
}

/** Central mapping from IntegrationId to display metadata */
export const INTEGRATION_META: Record<
  Exclude<IntegrationId, 'NONE'>,
  IntegrationMeta
> = {
  HUBSPOT: {
    label: 'HubSpot',
    category: 'CRM',
    Icon: HubSpotIcon,
    Logo: HubSpotLogo,
  },
  SFDC: {
    label: 'Salesforce',
    category: 'CRM',
    Icon: SalesforceIcon,
    Logo: SalesforceLogo,
  },
  NETSUITE: {
    label: 'NetSuite',
    category: 'ERP',
    Icon: NetSuiteIcon,
    Logo: NetSuiteLogo,
  },
  QBO: {
    label: 'QuickBooks',
    category: 'ERP',
    Icon: QuickBooksOnlineIcon,
    Logo: QuickBooksOnlineLogo,
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Check whether an integration ID is present in an array of integrations */
export function hasIntegration(
  integrations: Integration[],
  id: IntegrationId
): boolean {
  return integrations.some((i) => i.id === id);
}

/** Find an entry by its `type` discriminator from a typed array of discriminated-union entries */
export function findIntegrationConfig<
  TEntry extends { type: string },
  T extends TEntry['type'],
>(configs: TEntry[], type: T): Extract<TEntry, { type: T }> | null {
  const found = configs.find((c) => c.type === type);
  if (!found) {
    return null;
  }
  return found as Extract<TEntry, { type: T }>;
}
